import { useEffect, useState } from 'react'
import { supabase } from '../supabaseClient'
import { useAuth } from '../context/AuthContext'
import { useNavigate } from 'react-router-dom'
import Navbar from '../components/Navbar'
import { logAction } from '../lib/audit'

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']

export default function PatientDashboard() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [allAssignments, setAllAssignments] = useState([])
  const [allInterventionAssignments, setAllInterventionAssignments] = useState([])
  const [completions, setCompletions] = useState([])
  const [interventionCompletions, setInterventionCompletions] = useState([])
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [weekOffset, setWeekOffset] = useState(0)

  const clinicTimezone = profile?.clinics?.timezone || 'America/New_York'
  const todayStr = new Date().toLocaleDateString('en-CA', { timeZone: clinicTimezone })
  const selectedStr = selectedDate.toLocaleDateString('en-CA', { timeZone: clinicTimezone })

  useEffect(() => { fetchData() }, [])
  useEffect(() => { fetchCompletionsForDate() }, [selectedStr])

  async function fetchData() {
    const [{ data: assignmentData }, { data: interventionData }, { data: sub }] = await Promise.all([
      supabase.from('assignments').select('*, exercises(*)').eq('patient_id', profile.id).order('sort_order'),
      supabase.from('intervention_assignments').select('*, interventions(*)').eq('patient_id', profile.id).order('sort_order'),
      supabase.from('subscriptions').select('*').eq('clinic_id', profile.clinic_id).single()
    ])
    setAllAssignments(assignmentData || [])
    setAllInterventionAssignments(interventionData || [])
    setSubscription(sub)
    setLoading(false)
  }

  async function fetchCompletionsForDate() {
    const [{ data: completionData }, { data: interventionCompletionData }] = await Promise.all([
      supabase.from('completions').select('*').eq('patient_id', profile.id).eq('completed_on', selectedStr),
      supabase.from('intervention_completions').select('*').eq('patient_id', profile.id).eq('completed_on', selectedStr)
    ])
    setCompletions(completionData || [])
    setInterventionCompletions(interventionCompletionData || [])
  }

  function isClinicActive() {
    if (!subscription) return true
    if (subscription.status === 'active') return true
    if (subscription.status === 'trialing') return new Date(subscription.trial_ends_at) > new Date()
    return false
  }

  function getEndDate(assignment) {
    if (!assignment.num_weeks || !assignment.assigned_at) return null
    const end = new Date(assignment.assigned_at)
    end.setDate(end.getDate() + assignment.num_weeks * 7)
    return end
  }

  function getProgramEndDate() {
    const allItems = [...allAssignments, ...allInterventionAssignments]
    const ends = allItems.map(a => getEndDate(a)).filter(Boolean)
    if (ends.length === 0) return null
    return new Date(Math.max(...ends.map(d => d.getTime())))
  }

  function getWeeksRemaining() {
    const end = getProgramEndDate()
    if (!end) return null
    const diff = end - new Date()
    if (diff <= 0) return 0
    return Math.ceil(diff / (1000 * 60 * 60 * 24 * 7))
  }

  function filterByDate(items, date) {
    const dayOfWeek = date.getDay()
    return items.filter(a => {
      if (a.frequency === 'daily') return true
      if (a.frequency === 'custom' && a.days_of_week) return a.days_of_week.includes(dayOfWeek)
      return true
    }).filter(a => {
      const end = getEndDate(a)
      if (!end) return true
      return date <= end
    })
  }

  function getWeekDays() {
    const today = new Date()
    const start = new Date(today)
    start.setDate(today.getDate() - today.getDay() + weekOffset * 7)
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start)
      d.setDate(start.getDate() + i)
      return d
    })
  }

  function isExerciseCompleted(assignmentId) {
    return completions.some(c => c.assignment_id === assignmentId)
  }

  function isInterventionCompleted(assignmentId) {
    return interventionCompletions.some(c => c.intervention_assignment_id === assignmentId)
  }

  const isToday = selectedStr === todayStr
  const isPast = selectedDate < new Date(todayStr)
  const isFuture = selectedStr > todayStr

  async function toggleExercise(assignment) {
    if (isFuture) return
    const completed = isExerciseCompleted(assignment.id)
    if (completed) {
      await supabase.from('completions').delete().eq('assignment_id', assignment.id).eq('completed_on', selectedStr)
      setCompletions(prev => prev.filter(c => c.assignment_id !== assignment.id))
    } else {
      const { data } = await supabase.from('completions').insert({
        patient_id: profile.id, assignment_id: assignment.id, completed_on: selectedStr
      }).select().single()
      setCompletions(prev => [...prev, data])
      logAction(profile.id, 'complete_exercise', { assignment_id: assignment.id, date: selectedStr })
    }
  }

  async function toggleIntervention(assignment) {
    if (isFuture) return
    const completed = isInterventionCompleted(assignment.id)
    if (completed) {
      await supabase.from('intervention_completions').delete().eq('intervention_assignment_id', assignment.id).eq('completed_on', selectedStr)
      setInterventionCompletions(prev => prev.filter(c => c.intervention_assignment_id !== assignment.id))
    } else {
      const { data } = await supabase.from('intervention_completions').insert({
        patient_id: profile.id, intervention_assignment_id: assignment.id, completed_on: selectedStr
      }).select().single()
      setInterventionCompletions(prev => [...prev, data])
    }
  }

  const weekDays = getWeekDays()
  const assignments = filterByDate(allAssignments, selectedDate)
  const interventionAssignments = filterByDate(allInterventionAssignments, selectedDate)
  const totalItems = assignments.length + interventionAssignments.length
  const completedItems = assignments.filter(a => isExerciseCompleted(a.id)).length + interventionAssignments.filter(a => isInterventionCompleted(a.id)).length
  const allDone = completedItems === totalItems && totalItems > 0
  const pct = totalItems ? Math.round((completedItems / totalItems) * 100) : 0
  const programEnd = getProgramEndDate()
  const weeksRemaining = getWeeksRemaining()

  if (loading) return <><Navbar /><div className="page"><p>Loading...</p></div></>

  if (!isClinicActive()) {
    return (
      <>
        <Navbar />
        <div className="page" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: '1rem' }}>⏸️</div>
          <h1 style={{ marginBottom: 8 }}>Program paused</h1>
          <p style={{ maxWidth: 300, margin: '0 auto' }}>Your clinic subscription has lapsed. Please contact your clinician to resume your program.</p>
        </div>
      </>
    )
  }

  return (
    <>
      <Navbar />
      <div className="page">
        <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>Hi, {profile?.full_name?.split(' ')[0]} 👋</h1>
            <p>{new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="secondary" onClick={() => navigate('/patient/history')} style={{ fontSize: 13, padding: '7px 12px', marginTop: 4 }}>📅 History</button>
            <button className="secondary" onClick={() => navigate('/patient/schedule')} style={{ fontSize: 13, padding: '7px 12px', marginTop: 4 }}>Export</button>
          </div>
        </div>

        {programEnd && (
          <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem 1.25rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div>
                <p style={{ fontWeight: 600, color: 'var(--text-primary)', margin: '0 0 2px', fontSize: 14 }}>Program timeline</p>
                <p style={{ margin: 0, fontSize: 13 }}>
                  {weeksRemaining > 0 ? weeksRemaining + ' week' + (weeksRemaining !== 1 ? 's' : '') + ' remaining' : 'Program complete'}
                  {' ends ' + MONTH_LABELS[programEnd.getMonth()] + ' ' + programEnd.getDate()}
                </p>
              </div>
              {weeksRemaining > 0 && (
                <div style={{ width: 52, height: 52, borderRadius: '50%', flexShrink: 0, background: 'var(--brand-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                  <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--brand)', lineHeight: 1 }}>{weeksRemaining}</span>
                  <span style={{ fontSize: 10, color: 'var(--brand)', fontWeight: 600 }}>wks</span>
                </div>
              )}
            </div>
            <div style={{ height: 6, background: 'var(--border)', borderRadius: 99 }}>
              <div style={{
                height: 6, borderRadius: 99, background: 'var(--brand)',
                width: (() => {
                  const allItems = [...allAssignments, ...allInterventionAssignments]
                  if (!allItems[0]?.assigned_at) return '0%'
                  const start = new Date(allItems[0].assigned_at)
                  const total = programEnd - start
                  const elapsed = new Date() - start
                  return Math.min(100, Math.max(0, Math.round((elapsed / total) * 100))) + '%'
                })(),
                transition: 'width 0.4s ease'
              }} />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
              <p style={{ fontSize: 11, margin: 0, color: 'var(--text-tertiary)' }}>
                Started {new Date([...allAssignments, ...allInterventionAssignments][0]?.assigned_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
              <p style={{ fontSize: 11, margin: 0, color: 'var(--text-tertiary)' }}>
                Ends {programEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </p>
            </div>
          </div>
        )}

        <div className="card" style={{ marginBottom: '1.5rem', padding: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <button type="button" onClick={() => setWeekOffset(w => w - 1)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1, flexShrink: 0 }}>‹</button>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, flex: 1 }}>
            {weekDays.map((day, i) => {
              const dayStr = day.toISOString().split('T')[0]
              const isSelected = dayStr === selectedStr
              const isCurrentDay = dayStr === todayStr
              const dayAssignments = filterByDate(allAssignments, day)
              const dayInterventions = filterByDate(allInterventionAssignments, day)
              const hasItems = dayAssignments.length > 0 || dayInterventions.length > 0
              return (
                <button key={i} type="button" onClick={() => setSelectedDate(day)} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                  padding: '8px 4px', borderRadius: 10, border: 'none',
                  background: isSelected ? 'var(--brand)' : isCurrentDay ? 'var(--brand-light)' : 'transparent',
                  color: isSelected ? 'white' : isCurrentDay ? 'var(--brand)' : 'var(--text-secondary)',
                  cursor: 'pointer', transition: 'all 0.15s'
                }}>
                  <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{DAY_LABELS[day.getDay()]}</span>
                  <span style={{ fontSize: 18, fontWeight: 700 }}>{day.getDate()}</span>
                  <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{day.toLocaleDateString('en-US', { month: 'short' })}</span>
                  <div style={{ width: 6, height: 6, borderRadius: '50%', background: hasItems ? isSelected ? 'rgba(255,255,255,0.6)' : 'var(--brand)' : 'transparent' }} />
                </button>
              )
            })}
          </div>
            <button type="button" onClick={() => setWeekOffset(w => w + 1)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 6px', borderRadius: 6, color: 'var(--text-secondary)', fontSize: 18, lineHeight: 1, flexShrink: 0 }}>›</button>
          </div>
        </div>

        <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <p style={{ fontWeight: 600, color: 'var(--text-primary)', fontSize: 15, margin: 0 }}>
            {isToday ? "Today's program" : selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
          </p>
          {!isToday && (
            <button type="button" className="secondary" onClick={() => setSelectedDate(new Date())} style={{ fontSize: 12, padding: '6px 12px' }}>
              Back to today
            </button>
          )}
        </div>

        {totalItems > 0 && (
          <div className="card" style={{
            marginBottom: '1.5rem',
            background: allDone ? 'linear-gradient(135deg, #22c55e, #16a34a)' : 'linear-gradient(135deg, #6366f1, #4f46e5)',
            border: 'none'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 600, margin: 0 }}>
                {allDone ? 'All done!' : 'Progress'}
              </p>
              <p style={{ color: 'white', fontWeight: 800, fontSize: 20, margin: 0 }}>{completedItems}/{totalItems}</p>
            </div>
            <div style={{ height: 8, background: 'rgba(255,255,255,0.25)', borderRadius: 99 }}>
              <div style={{ height: 8, borderRadius: 99, background: 'white', width: pct + '%', transition: 'width 0.4s ease' }} />
            </div>
            {allDone && isToday && (
              <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: 13, marginTop: 10, marginBottom: 0 }}>
                Great work! Your clinician can see your progress.
              </p>
            )}
          </div>
        )}

        {totalItems === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">🎉</div>
            <h2 style={{ marginBottom: 8 }}>No items {isToday ? 'today' : 'this day'}</h2>
            <p>{isToday ? 'Enjoy your rest day!' : 'Nothing scheduled for this day.'}</p>
          </div>
        )}

        {!isToday && !isPast && totalItems > 0 && (
          <div style={{ background: 'var(--brand-light)', border: '1px solid #c7d2fe', borderRadius: 10, padding: '10px 14px', marginBottom: '1rem', fontSize: 13, color: 'var(--brand)', fontWeight: 500 }}>
            Viewing a future day — check off items on the day itself.
          </div>
        )}

        {isPast && totalItems > 0 && (
          <div style={{ background: '#fafafa', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', marginBottom: '1rem', fontSize: 13, color: 'var(--text-secondary)' }}>
            Viewing a past day — you can still check off any missed exercises.
          </div>
        )}

        {assignments.length > 0 && (
          <>
            <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Exercises</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 20 }}>
              {assignments.map(assignment => {
                const done = isExerciseCompleted(assignment.id)
                const end = getEndDate(assignment)
                return (
                  <div key={assignment.id} className="card" style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    border: '1px solid ' + (done ? '#bbf7d0' : 'var(--border)'),
                    background: done ? '#f0fdf4' : 'var(--surface)',
                    transition: 'all 0.2s', opacity: (!isToday && !done) ? 0.7 : 1
                  }}>
                    <div onClick={() => toggleExercise(assignment)} style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      border: '2px solid ' + (done ? 'var(--success)' : 'var(--border-strong)'),
                      background: done ? 'var(--success)' : 'transparent',
                      cursor: !isFuture ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
                    }}>
                      {done && <span style={{ color: 'white', fontSize: 15, fontWeight: 700 }}>✓</span>}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: done ? '#15803d' : 'var(--text-primary)', textDecoration: done ? 'line-through' : 'none' }}>
                        {assignment.exercises.title}
                      </p>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {(assignment.sets || assignment.exercises.sets) && <span className="badge" style={{ background: done ? '#dcfce7' : undefined, color: done ? '#15803d' : undefined }}>{assignment.sets || assignment.exercises.sets} sets</span>}
                        {(assignment.reps || assignment.exercises.reps) && <span className="badge" style={{ background: done ? '#dcfce7' : undefined, color: done ? '#15803d' : undefined }}>{assignment.reps || assignment.exercises.reps} reps</span>}
                        {(assignment.duration_seconds || assignment.exercises.duration_seconds) && <span className="badge" style={{ background: done ? '#dcfce7' : undefined, color: done ? '#15803d' : undefined }}>{assignment.duration_seconds || assignment.exercises.duration_seconds}s</span>}
                        {end && <span className="badge" style={{ background: '#fafafa', color: 'var(--text-tertiary)' }}>until {MONTH_LABELS[end.getMonth()]} {end.getDate()}</span>}
                      </div>
                      {(assignment.exercises?.description || assignment.exercises?.instructions) && (
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                          {assignment.exercises?.instructions || assignment.exercises?.description}
                        </p>
                      )}
                      {assignment.exercises?.video_url && (
                        <a href={assignment.exercises.video_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--brand)', fontWeight: 600, marginTop: 6, textDecoration: 'none' }}>
                          ▶ Watch video
                        </a>
                      )}
                      {assignment.clinician_note && <p style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>Note: {assignment.clinician_note}</p>}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}

        {interventionAssignments.length > 0 && (
          <>
            <p style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 10 }}>Tasks</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {interventionAssignments.map(assignment => {
                const done = isInterventionCompleted(assignment.id)
                return (
                  <div key={assignment.id} className="card" style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    border: '1px solid ' + (done ? '#bbf7d0' : 'var(--border)'),
                    background: done ? '#f0fdf4' : 'var(--surface)',
                    transition: 'all 0.2s', opacity: (!isToday && !done) ? 0.7 : 1
                  }}>
                    <div onClick={() => toggleIntervention(assignment)} style={{
                      width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                      border: '2px solid ' + (done ? 'var(--success)' : 'var(--border-strong)'),
                      background: done ? 'var(--success)' : 'transparent',
                      cursor: !isFuture ? 'pointer' : 'default',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s'
                    }}>
                      {done && <span style={{ color: 'white', fontSize: 15, fontWeight: 700 }}>✓</span>}
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontWeight: 600, fontSize: 15, marginBottom: 4, color: done ? '#15803d' : 'var(--text-primary)', textDecoration: done ? 'line-through' : 'none' }}>
                        {assignment.interventions.title}
                      </p>
                      {(assignment.exercises?.description || assignment.exercises?.instructions) && (
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                          {assignment.exercises?.instructions || assignment.exercises?.description}
                        </p>
                      )}
                      {(assignment.interventions?.instructions || assignment.interventions?.description) && (
                        <p style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                          {assignment.interventions?.instructions || assignment.interventions?.description}
                        </p>
                      )}
                      {assignment.exercises?.video_url && (
                        <a href={assignment.exercises.video_url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--brand)', fontWeight: 600, marginTop: 6, textDecoration: 'none' }}>
                          ▶ Watch video
                        </a>
                      )}
                      {assignment.clinician_note && <p style={{ fontSize: 12, fontStyle: 'italic', marginTop: 4 }}>Note: {assignment.clinician_note}</p>}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </div>
    </>
  )
}